import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShoppingCart, Trash, Printer, DownloadSimple, EnvelopeSimple,
  Copy, Pencil, PushPin, PushPinSlash, CaretRight, Question,
  DotsThreeVertical, ArrowsClockwise, X, ArrowsLeftRight, Plus, UsersThree,
} from "@phosphor-icons/react";
import AutoSeo from "@/components/AutoSeo";
import { AlternativesPanel } from "@/components/shopping/AlternativesPanel";
import { AmazonBulkBar } from "@/components/shopping/AmazonBulkBar";
import { ShareDialog } from "@/components/sharing/ShareDialog";
import { ListSwitcher } from "@/components/shopping/ListSwitcher";
import { ShoppingMode } from "@/components/shopping/ShoppingMode";
import { EmailShareDialog } from "@/components/EmailShareDialog";
import useShoppingSync, { activityText } from "@/hooks/useShoppingSync";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import PageMeta from "@/components/PageMeta";
import { useVoiceCommand, useVoiceSearch } from "@/contexts/VoiceSearchContext";
import {
  splitChained, parseOne, findMergeTarget, formatQtyPhrase, mergeTotals,
  parseMergeDecision, stripAddShell,
} from "@/utils/shoppingVoiceParser";
import { speak as rfSpeak, okPing } from "@/lib/voice";

/*  Recipe Filer — Shopping List (Phase 1 + Phase 2)
 *
 *  Landing: /shopping (auth required, added to the Dashboard tab bar).
 *
 *  Sections in visual order:
 *    1. Header + export toolbar (JPG, PDF, Email, Copy)
 *    2. Aisle-grouped ticklist. Each row shows:
 *         [ ] {qty} {display_name}   · from Recipe A + Recipe B
 *                                       [Buy · Amazon]  (only pantry aisles)
 *       Long-press / hover shows a small pop-over: rename Amazon query
 *       (killer feature), toggle staple, delete.
 *    3. Pantry staples strip — "always-have" items auto-hidden from the
 *       working list. One tap to unmark.
 *
 *  Exports are frontend-only (image via html2canvas, PDF via jsPDF) — no
 *  server render needed. Email goes through /api/shopping-list/email
 *  (Resend on the backend).
 */

const AISLES = [
  { key: "produce", label: "Fresh produce", emoji: "🥬" },
  { key: "meat",    label: "Meat & fish",   emoji: "🥩" },
  { key: "dairy",   label: "Dairy & eggs",  emoji: "🥛" },
  { key: "bakery",  label: "Bakery",        emoji: "🥖" },
  { key: "frozen",  label: "Frozen",        emoji: "❄️" },
  { key: "baking",  label: "Baking",        emoji: "🧁" },
  { key: "spices",  label: "Spices & seasoning", emoji: "🌶" },
  { key: "pantry",  label: "Pantry",        emoji: "🥫" },
  { key: "drinks",  label: "Drinks",        emoji: "🍷" },
  { key: "other",   label: "Other",         emoji: "🛒" },
];

const REMOVE_RE = /\b(?:remove|delete|drop|get\s+rid\s+of)\s+(?!(?:all\s+)?(?:the\s+)?(?:bought|ticked|checked|done)\b)(?:the\s+)?(.+?)(?:\s+(?:from|off)\s+(?:the\s+|my\s+)?(?:shopping\s+)?list)?\.?$|\btake\s+(?:the\s+)?(.+?)\s+off\s+(?:the\s+|my\s+)?(?:shopping\s+)?list\b/i;

export default function ShoppingList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [staples, setStaples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editQuery, setEditQuery] = useState("");
  const [saveAsPref, setSaveAsPref] = useState(true);
  const [aisleMenuFor, setAisleMenuFor] = useState(null);   // item_id whose "Move to aisle" sheet is open
  const [altsFor, setAltsFor] = useState(null);
  const [amazonMode, setAmazonMode] = useState(false);
  const [amazonSel, setAmazonSel] = useState(() => new Set());
  const [quickAdd, setQuickAdd] = useState("");
  const [listOwner, setListOwner] = useState(() => new URLSearchParams(window.location.search).get("list") || localStorage.getItem("rf_shop_list_id") || "");
  const [shares, setShares] = useState({ owned: [], shared: [] });
  const [role, setRole] = useState("owner");
  const [listName, setListName] = useState("Shopping list");
  const [offline, setOffline] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [shopMode, setShopMode] = useState(() => new URLSearchParams(window.location.search).get("mode") === "shop");
  const closeShopMode = useCallback(() => {
    setShopMode(false);
    const u = new URL(window.location.href);
    if (u.searchParams.has("mode")) { u.searchParams.delete("mode"); window.history.replaceState(null, "", u.pathname + u.search); }
  }, []);
  const { flags } = useSiteFlags();
  const listRef = useRef(null);
  const ownerRef = useRef(listOwner);
  ownerRef.current = listOwner;
  const lq = () => (ownerRef.current ? { params: { list_id: ownerRef.current } } : {});

  const loadShares = async () => {
    try {
      const { data } = await api.get("/shopping-lists");
      setShares(data);
      const cur = ownerRef.current;
      if (cur && !data.owned.some((l) => l.list_id === cur) && !data.shared.some((j) => j.resource_id === cur)) switchList("");
    } catch { /* non-critical */ }
  };

  const applyList = (data) => {
    setItems(data.items || []);
    setStaples(data.staples || []);
    setIsShared(!!data.is_shared);
    setRole(data.role || "owner");
    setListName(data.list_name || "Shopping list");
  };

  const load = async () => {
    const cacheKey = `rf_shop_cache_${user?.user_id || "anon"}_${ownerRef.current || "main"}`;
    try {
      const { data } = await api.get("/shopping-list", lq());
      applyList(data);
      setOffline(false);
      try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch { /* storage full */ }
    } catch (e) {
      if ((e?.response?.status === 403 || e?.response?.status === 404) && ownerRef.current) { switchList(""); return; }
      const cached = localStorage.getItem(cacheKey);
      if (!e?.response && cached) { applyList(JSON.parse(cached)); setOffline(true); }
      else toast.error("Couldn't load your shopping list");
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadShares(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function switchList(owner) {
    ownerRef.current = owner;
    setListOwner(owner);
    if (owner) localStorage.setItem("rf_shop_list_id", owner);
    else localStorage.removeItem("rf_shop_list_id");
    setAmazonMode(false); setAltsFor(null);
    load();
  }

  useShoppingSync({
    enabled: isShared || role !== "owner",
    listOwner,
    onEvents: (events) => {
      events.forEach((ev) => toast(activityText(ev), { icon: <UsersThree size={16} weight="duotone" /> }));
      load();
    },
  });

  // Close the aisle-move menu when clicking anywhere else on the page.
  useEffect(() => {
    if (!aisleMenuFor) return undefined;
    const onDocClick = () => setAisleMenuFor(null);
    // Delay attach so the click that opened the menu doesn't immediately close it.
    const id = setTimeout(() => document.addEventListener("click", onDocClick), 0);
    return () => { clearTimeout(id); document.removeEventListener("click", onDocClick); };
  }, [aisleMenuFor]);

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const a = it.aisle || "other";
      (map[a] = map[a] || []).push(it);
    });
    return map;
  }, [items]);

  const uncheckedCount = items.filter((i) => !i.checked).length;
  const checkedCount = items.length - uncheckedCount;

  const toggleCheck = async (item) => {
    // Optimistic
    setItems((prev) => prev.map((i) => i.item_id === item.item_id ? { ...i, checked: !i.checked } : i));
    try { await api.patch(`/shopping-list/${item.item_id}`, { checked: !item.checked }); }    catch { toast.error("Couldn't update — undoing"); load(); }
  };

  const markStaple = async (item, is_staple) => {
    try {
      await api.patch(`/shopping-list/${item.item_id}`, { is_staple });
      toast.success(is_staple ? `"${item.display_name}" marked as always-have` : "Removed from pantry");
      load();
    } catch { toast.error("Couldn't update"); }
  };

  const deleteItem = async (item) => {
    setItems((prev) => prev.filter((i) => i.item_id !== item.item_id));
    try { await api.delete(`/shopping-list/${item.item_id}`); }
    catch { toast.error("Couldn't delete — undoing"); load(); }
  };

  const swapItem = async (item, newName) => {
    try {
      await api.patch(`/shopping-list/${item.item_id}`, { display_name: newName, amazon_query: newName, remember_swap: true });
      toast.success(`Swapped ${item.display_name} for ${newName}`);
      setAltsFor(null);
      load();
    } catch { toast.error("Couldn't swap"); }
  };

  const submitQuickAdd = async (e) => {
    e.preventDefault();
    const names = splitChained(quickAdd.trim()).map(parseOne).filter((p) => p.name && p.name.length >= 2);
    if (!names.length) return;
    try {
      for (const p of names) await api.post("/shopping-list/add-manual", { name: p.name, quantity: p.qty, unit: p.unit }, lq());
      toast.success(`Added ${names.map((p) => p.name).join(", ")}`);
      setQuickAdd("");
      load();
    } catch { toast.error("Couldn't add"); }
  };

  const amazonEligible = items.filter((i) => !i.checked && i.purchase_hint !== "have" && i.amazon_url);
  const startAmazonMode = () => { setAmazonSel(new Set(amazonEligible.map((i) => i.item_id))); setAmazonMode(true); };
  const toggleAmazonSel = (id) => setAmazonSel((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  /* Layer 2 — user moves an item to a different aisle. Optimistic UI,
   * server also stores the choice on the user's account so future imports
   * of the same ingredient land here automatically, and applies retroactively
   * to every other unchecked row with the same normalised name. */
  const moveAisle = async (item, newAisle) => {
    if (newAisle === item.aisle) { setAisleMenuFor(null); return; }
    const norm = item.normalised_name;
    // Optimistic — flip every unchecked row with the same normalised name
    setItems((prev) => prev.map((i) =>
      (i.normalised_name === norm && !i.checked) ? { ...i, aisle: newAisle } : i
    ));
    setAisleMenuFor(null);
    try {
      await api.patch(`/shopping-list/${item.item_id}`, { aisle: newAisle });
      const label = AISLES.find((a) => a.key === newAisle)?.label || newAisle;
      toast.success(`Moved to ${label} — we'll remember for next time`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move — undoing");
      load();
    }
  };

  const clearChecked = async () => {
    if (!checkedCount) return;
    try {
      const { data } = await api.post("/shopping-list/clear-checked", null, lq());
      toast.success(`Cleared ${data.removed} ticked ${data.removed === 1 ? "item" : "items"}`);
      load();
    } catch { toast.error("Couldn't clear"); }
  };

  // ---------- Voice commands (P5 — useVoiceCommand) ----------
  // Keep handlers in a ref so re-renders of `items` don't resubscribe
  // on every state change (which would thrash the global voice context).
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Find a shopping list item by voice-spoken name using the same tolerant
  // matching that the resolver uses: lower-case substring + normalised
  // name match. Returns the FIRST unchecked match (or first match if all
  // are checked) so "mark eggs" with 2 eggs rows gets the unticked one.
  const findItemByName = useCallback((raw) => {
    const q = (raw || "").toLowerCase().replace(/[^a-z0-9\s]+/g, " ").replace(/\s+/g, " ").trim();
    if (!q || q.length < 2) return null;
    const list = itemsRef.current || [];
    const matchers = [
      (it) => (it.display_name || "").toLowerCase() === q,
      (it) => (it.normalised_name || "").toLowerCase() === q,
      (it) => (it.display_name || "").toLowerCase().includes(q),
      (it) => (it.normalised_name || "").toLowerCase().includes(q),
    ];
    for (const fn of matchers) {
      const unchecked = list.find((i) => !i.checked && fn(i));
      if (unchecked) return unchecked;
      const anyMatch = list.find(fn);
      if (anyMatch) return anyMatch;
    }
    return null;
  }, []);

  // ---------- Voice helpers: chained utterances ----------
  // "add bread and milk" / "mark eggs and milk as bought" — split a
  // phrase into individual items so one voice command can act on many.
  // The CHAIN_DELIMS + splitChained helper lives at module scope so the
  // function identity is stable across renders.

  // Voice: mark bought — "mark eggs as bought", "mark eggs and milk as bought",
  // "tick off eggs and milk", "check off bread", "bought milk"
  const voiceMarkBought = useCallback((txt) => {
    const m = txt.match(/\b(?:mark|tick(?:\s+off)?|check(?:\s+off)?)\s+(.+?)\s+(?:as\s+)?(?:bought|done|got|got\s+it|complete|ticked|checked)\b/i)
         || txt.match(/\btick\s+off\s+(.+?)$/i)
         || txt.match(/\bcheck\s+off\s+(.+?)$/i)
         || txt.match(/\b(?:bought|got|ticked|checked)\s+(?:the\s+|some\s+)?(.+?)(?:\s+off)?$/i);
    const subject = (m?.[1] || "").trim();
    if (!subject) { toast.error("Which item?"); return; }
    const names = splitChained(subject);
    const ticked = []; const already = []; const missed = [];
    for (const n of names) {
      const item = findItemByName(n);
      if (!item) { missed.push(n); continue; }
      if (item.checked) { already.push(item.display_name); continue; }
      toggleCheck(item); ticked.push(item.display_name);
    }
    const parts = [];
    if (ticked.length) parts.push(`Ticked off ${ticked.join(", ")}`);
    if (already.length) parts.push(`${already.join(", ")} already ticked`);
    if (missed.length) parts.push(`Couldn't find: ${missed.join(", ")}`);
    (missed.length && !ticked.length ? toast.error : toast.success)(parts.join(" · "));
  }, [findItemByName]);

  // Voice: unmark — "unmark eggs", "unmark eggs and milk"
  const voiceUnmark = useCallback((txt) => {
    const m = txt.match(/\b(?:unmark|untick|uncheck|put(?:\s+back)?)\s+(?:the\s+)?(.+?)(?:\s+back(?:\s+on\s+the\s+list)?)?$/i);
    const subject = (m?.[1] || "").trim();
    if (!subject) { toast.error("Which item?"); return; }
    const names = splitChained(subject);
    const unticked = []; const already = []; const missed = [];
    for (const n of names) {
      const item = findItemByName(n);
      if (!item) { missed.push(n); continue; }
      if (!item.checked) { already.push(item.display_name); continue; }
      toggleCheck(item); unticked.push(item.display_name);
    }
    const parts = [];
    if (unticked.length) parts.push(`Put ${unticked.join(", ")} back on the list`);
    if (already.length) parts.push(`${already.join(", ")} already on the list`);
    if (missed.length) parts.push(`Couldn't find: ${missed.join(", ")}`);
    (missed.length && !unticked.length ? toast.error : toast.success)(parts.join(" · "));
  }, [findItemByName]);

  // Voice: add new item(s) — supports chained utterances via splitChained:
  //   "add bread and milk"
  //   "add a loaf of bread, 2 litres of milk, and a bunch of parsley"
  //   "add half a dozen eggs and some bread"
  //
  // Merge flow: when the parsed item already lives on the list AND the
  // units are compatible (g↔kg, ml↔l, bare count↔count), we pause the
  // chain, speak "You already have X — say add, replace, delete, or
  // cancel" and wait for the follow-up utterance. "add" merges totals,
  // "replace" overwrites qty+unit, "delete" clears the row then adds
  // the new one, "cancel" skips. If the user doesn't reply within 6 s
  // we auto-cancel (TTS "Okay, cancelled") and continue the chain.
  const vs = useVoiceSearch();
  const { pushRecentChip, setPriorityHandler } = vs;

  // TTS helper routed through the shared `/app/frontend/src/lib/voice.js`
  // picker so the shopping list uses the SAME British English female
  // voice as Kitchen View and the site-wide voice assistant — one
  // consistent "RecipeFiler voice".
  const speak = useCallback((text) => {
    try { rfSpeak(text); } catch { /* TTS unavailable — silent */ }
  }, []);

  const queueRef = useRef([]);           // remaining parsed items to process
  const processingRef = useRef(false);   // guard against concurrent runs
  const mergeTimerRef = useRef(null);    // 6 s "no reply → cancel" timer
  const pendingMergeRef = useRef(null);  // { parsed, existing }

  // ---- Voice undo stack --------------------------------------------------
  // Each voice-driven mutation pushes an inverse closure. "undo that" pops
  // and runs the newest one. Chained utterances push multiple entries so
  // the user can say "undo" repeatedly to peel back a whole batch.
  // Deliberately capped so a very long chain can't balloon memory.
  const UNDO_STACK_MAX = 20;
  const undoStackRef = useRef([]);
  const pushUndo = useCallback((label, fn) => {
    const stack = undoStackRef.current;
    stack.push({ label, fn });
    if (stack.length > UNDO_STACK_MAX) stack.shift();
  }, []);

  // Perform one of the four merge decisions on the active prompt.
  const applyMergeDecision = useCallback(async (decision) => {
    const pending = pendingMergeRef.current;
    if (!pending) return;
    pendingMergeRef.current = null;
    if (mergeTimerRef.current) { clearTimeout(mergeTimerRef.current); mergeTimerRef.current = null; }
    setPriorityHandler(null);
    const { parsed, existing } = pending;

    try {
      if (decision === "add") {
        const merged = mergeTotals(existing.quantity, existing.unit, parsed.qty, parsed.unit);
        if (merged) {
          const prevQty = existing.quantity, prevUnit = existing.unit;
          await api.patch(`/shopping-list/${existing.item_id}`, { quantity: merged.qty, unit: merged.unit });
          const phrase = formatQtyPhrase(merged.qty, merged.unit, existing.display_name);
          speak(`Updated — ${phrase}`);
          pushRecentChip(phrase, "ok");
          pushUndo(`merge ${existing.display_name}`, async () => {
            await api.patch(`/shopping-list/${existing.item_id}`, { quantity: prevQty, unit: prevUnit });
          });
        } else {
          // Mismatched units slipped through — fall back to a brand new row.
          const { data } = await api.post("/shopping-list/add-manual", { name: parsed.name, quantity: parsed.qty, unit: parsed.unit }, lq());
          speak(`Added ${formatQtyPhrase(parsed.qty, parsed.unit, parsed.name)}`);
          pushRecentChip(parsed.name, "ok");
          if (data?.item_id && data?.merged === false) {
            pushUndo(`add ${parsed.name}`, async () => { await api.delete(`/shopping-list/${data.item_id}`); });
          }
        }
      } else if (decision === "replace") {
        const prevQty = existing.quantity, prevUnit = existing.unit;
        await api.patch(`/shopping-list/${existing.item_id}`, { quantity: parsed.qty, unit: parsed.unit });
        const phrase = formatQtyPhrase(parsed.qty, parsed.unit, existing.display_name);
        speak(`Replaced — ${phrase}`);
        pushRecentChip(`replaced ${existing.display_name}`, "info");
        pushUndo(`replace ${existing.display_name}`, async () => {
          await api.patch(`/shopping-list/${existing.item_id}`, { quantity: prevQty, unit: prevUnit });
        });
      } else if (decision === "delete") {
        const prevName = existing.display_name, prevQty = existing.quantity, prevUnit = existing.unit;
        await api.delete(`/shopping-list/${existing.item_id}`);
        const { data } = await api.post("/shopping-list/add-manual", { name: parsed.name, quantity: parsed.qty, unit: parsed.unit }, lq());
        const phrase = formatQtyPhrase(parsed.qty, parsed.unit, parsed.name);
        speak(`Deleted the old one — added ${phrase}`);
        pushRecentChip(`restarted ${parsed.name}`, "info");
        const newItemId = data?.item_id;
        pushUndo(`restart ${parsed.name}`, async () => {
          if (newItemId) { try { await api.delete(`/shopping-list/${newItemId}`); } catch {} }
          await api.post("/shopping-list/add-manual", { name: prevName, quantity: prevQty, unit: prevUnit }, lq());
        });
      } else { // cancel
        speak("Okay, cancelled");
        pushRecentChip(`skipped ${parsed.name}`, "warn");
      }
    } catch (e) {
      speak("Sorry, that didn't save");
      toast.error(e?.response?.data?.detail || "Voice merge failed");
    }
    load();
    // Allow the queue runner to resume.
    processingRef.current = false;
    runQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speak, pushRecentChip, setPriorityHandler, pushUndo]);

  // Priority handler is installed while a merge prompt is on-screen so
  // the global voice dispatcher routes the next utterance straight to
  // the decision parser (bypasses tier 1/2/3/4). Returning `true` tells
  // the dispatcher we consumed it.
  const makeMergeHandler = useCallback(() => async (txt) => {
    const d = parseMergeDecision(txt);
    if (!d) {
      speak("Say add, replace, delete, or cancel");
      return true; // consume anyway — we're waiting for a valid reply
    }
    await applyMergeDecision(d);
    return true;
  }, [applyMergeDecision, speak]);

  const runQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    while (queueRef.current.length > 0) {
      const parsed = queueRef.current.shift();
      if (!parsed || !parsed.name || parsed.name.length < 2) continue;

      const existing = findMergeTarget(itemsRef.current || [], parsed);
      if (existing) {
        // Pause the queue — show prompt, hand control to the priority
        // handler. runQueue() will be re-invoked by applyMergeDecision.
        pendingMergeRef.current = { parsed, existing };
        const existingPhrase = formatQtyPhrase(existing.quantity, existing.unit, existing.display_name);
        const newPhrase = formatQtyPhrase(parsed.qty, parsed.unit, parsed.name);
        speak(`You already have ${existingPhrase}. Say add to make it total, replace with ${newPhrase}, delete to start again, or cancel.`);
        pushRecentChip(`decide: ${existing.display_name}`, "warn");
        setPriorityHandler(makeMergeHandler());
        if (mergeTimerRef.current) clearTimeout(mergeTimerRef.current);
        mergeTimerRef.current = setTimeout(() => {
          if (pendingMergeRef.current) applyMergeDecision("cancel");
        }, 6000);
        return; // runQueue resumes once the user replies / times out
      }

      // Normal add — no duplicate.
      try {
        const { data } = await api.post("/shopping-list/add-manual", {
          name: parsed.name, quantity: parsed.qty, unit: parsed.unit,
        }, lq());
        const phrase = parsed.qty
          ? formatQtyPhrase(parsed.qty, parsed.unit, parsed.name)
          : parsed.name;
        pushRecentChip(phrase, "ok");
        // Only when the backend created a brand-new row can we cleanly
        // undo with a DELETE. If the backend auto-merged (exact unit
        // match on same normalised name), undoing would need a before
        // snapshot we don't have — skip.
        if (data?.item_id && data?.merged === false) {
          pushUndo(`add ${parsed.name}`, async () => { await api.delete(`/shopping-list/${data.item_id}`); });
        }
      } catch {
        pushRecentChip(`failed: ${parsed.name}`, "warn");
      }
    }
    processingRef.current = false;
    load();
    // Queue drained cleanly (no pending merge) — give the cook an audible
    // "Anything else?" so they know we're ready for the next utterance.
    if (!pendingMergeRef.current) speak("Anything else?");
  }, [speak, pushRecentChip, setPriorityHandler, makeMergeHandler, applyMergeDecision, pushUndo]);

  const voiceAdd = useCallback(async (txt) => {
    const payload = stripAddShell(txt);
    if (!payload) { toast.error("What should I add?"); return; }
    const items = splitChained(payload).map(parseOne).filter((it) => it.name && it.name.length >= 2);
    if (items.length === 0) { toast.error("What should I add?"); return; }

    // Append to the running queue so a user chaining utterances in a
    // noisy kitchen never loses an item mid-flight.
    queueRef.current.push(...items);
    runQueue();
  }, [runQueue]);

  // Clear any pending merge prompt + 6s timer when the user navigates
  // away from the Shopping List mid-flight.
  useEffect(() => () => {
    if (mergeTimerRef.current) clearTimeout(mergeTimerRef.current);
    pendingMergeRef.current = null;
    queueRef.current = [];
    processingRef.current = false;
    undoStackRef.current = [];
    try { setPriorityHandler(null); } catch {}
  }, [setPriorityHandler]);

  // Voice: undo last — "undo", "undo that", "undo last", "scratch that",
  // "take that back". Pops the newest inverse closure and runs it. For a
  // chained utterance each item has its own entry so the cook can say
  // "undo, undo" to peel a two-item batch.
  const voiceUndo = useCallback(async () => {
    const entry = undoStackRef.current.pop();
    if (!entry) { speak("Nothing to undo"); pushRecentChip("nothing to undo", "warn"); return; }
    try {
      await entry.fn();
      speak(`Undone — ${entry.label}`);
      pushRecentChip(`undo: ${entry.label}`, "info");
      load();
    } catch (e) {
      speak("Sorry, that didn't undo");
      toast.error(e?.response?.data?.detail || "Undo failed");
      // Push it back so the user can retry.
      undoStackRef.current.push(entry);
    }
  }, [speak, pushRecentChip]);

  // Voice: remove — "remove eggs", "delete milk and bread", "take butter off the list"
  const voiceRemove = useCallback(async (txt) => {
    const m = txt.match(REMOVE_RE);
    const subject = (m?.[1] || m?.[2] || "").trim();
    if (!subject) { speak("Which item should I remove?"); return; }
    const removed = []; const missed = [];
    for (const n of splitChained(subject)) {
      const item = findItemByName(n);
      if (!item) { missed.push(n); continue; }
      itemsRef.current = itemsRef.current.filter((i) => i.item_id !== item.item_id);
      await deleteItem(item);
      removed.push(item.display_name);
      pushUndo(`remove ${item.display_name}`, async () => {
        await api.post("/shopping-list/add-manual", { name: item.display_name, quantity: item.quantity, unit: item.unit }, lq());
      });
      pushRecentChip(`removed ${item.display_name}`, "info");
    }
    const parts = [];
    if (removed.length) parts.push(`Removed ${removed.join(", ")}`);
    if (missed.length) parts.push(`Couldn't find: ${missed.join(", ")}`);
    if (removed.length) okPing();
    (missed.length && !removed.length ? toast.error : toast.success)(parts.join(" · "));
    speak(removed.length ? `Removed ${removed.join(" and ")}. Anything else?` : `I couldn't find ${missed.join(" or ")} on your list`);
  }, [findItemByName, pushUndo, pushRecentChip, speak]); // eslint-disable-line react-hooks/exhaustive-deps

  // Voice: clear ticked — "clear bought items", "clear ticked", "clean up the list"
  const voiceClearBought = useCallback(() => {
    clearChecked();
  }, [checkedCount]);  // eslint-disable-line react-hooks/exhaustive-deps

  useVoiceCommand(
    "shopping-mark-bought",
    /\b(?:mark|tick(?:\s+off)?|check(?:\s+off)?)\s+(.+?)\s+(?:as\s+)?(?:bought|done|got|complete|ticked|checked)\b|\b(?:tick|check)\s+off\s+\w+|\b(?:bought|got|ticked|checked)\s+(?:the\s+|some\s+)?\w+/i,
    voiceMarkBought,
    "Tick off item",
    { keepListening: true, chainPrompt: "Done. Anything else?" },
  );
  useVoiceCommand(
    "shopping-unmark",
    /\b(?:unmark|untick|uncheck|put(?:\s+back)?)\s+(?:the\s+)?\w+/i,
    voiceUnmark,
    "Put item back on list",
    { keepListening: true, chainPrompt: "Back on the list. Anything else?" },
  );
  useVoiceCommand(
    "shopping-add",
    /\badd\s+(?:(?:half\s+a\s+dozen|a\s+(?:dozen|couple|few|pair|loaf|bunch|bag|packet|pack|bottle|jar|tin|can|box|punnet|carton|block|head|clove|sprig|slice|knob|pinch|splash|dash|handful)\s+of\s+)|(?:\d+(?:\.\d+)?\s*(?:litres?|liters?|ml|g|kg|oz|lb|cups?|tbsp|tsp)?\s+(?:of\s+)?))?.+?\s+to\s+(?:the\s+|my\s+)?(?:shopping\s+)?list\b/i,
    voiceAdd,
    "Add to shopping list",
    // chainPrompt: false → voiceAdd / runQueue speak their own TTS so a
    // merge-prompt isn't talked over by a generic "Anything else?".
    // priority: beat the global "shopping list" page-route so adding on this page never just re-opens it.
    { keepListening: true, chainPrompt: false, priority: true },
  );
  useVoiceCommand(
    "shopping-clear",
    /\b(?:clear|clean\s+up|remove|delete)\s+(?:all\s+)?(?:bought|ticked|checked|done)\s+(?:items?|rows?)?\b|\bclear\s+(?:the\s+)?(?:ticked|checked|bought)\b/i,
    voiceClearBought,
    "Clear ticked items",
    { keepListening: false },
  );
  useVoiceCommand(
    "shopping-remove",
    REMOVE_RE,
    voiceRemove,
    "Remove item from list",
    { keepListening: true, chainPrompt: false, priority: true },
  );
  useVoiceCommand(
    "shopping-undo",
    /^(?:undo|scratch\s+that|revert\s+that)(?:\s+(?:that|last|it|the\s+last(?:\s+one)?))?\.?$/i,
    voiceUndo,
    "Undo last voice change",
    { keepListening: true, chainPrompt: false },
  );

  const saveAmazonPref = async (item) => {
    const q = editQuery.trim();
    if (!q) { setEditingItemId(null); return; }
    try {
      await api.patch(`/shopping-list/${item.item_id}`, {
        amazon_query: q,
        save_as_preference: saveAsPref,
      });
      toast.success(saveAsPref ? "Saved — every future list uses this" : "Updated for this item");
      setEditingItemId(null);
      load();
    } catch { toast.error("Couldn't save preference"); }
  };

  /* ---------- Exports ---------- */

  const buildTextForExport = () => {
    const lines = ["Your Recipe Filer shopping list", "-".repeat(34), ""];
    AISLES.forEach(({ key, label, emoji }) => {
      const rows = (grouped[key] || []).filter((i) => !i.checked);
      if (!rows.length) return;
      lines.push(`${emoji} ${label}`);
      rows.forEach((it) => {
        const q = it.quantity ? `${it.quantity} ${it.unit || ""} `.trim() + " " : "";
        const src = [...new Set((it.sources || []).map((s) => s.recipe_title).filter(Boolean))].join(", ");
        lines.push(`  [ ] ${q}${it.display_name}${src ? "  · from " + src : ""}`);
      });
      lines.push("");
    });
    return lines.join("\n").trim();
  };

  const copyList = async () => {
    try {
      await navigator.clipboard.writeText(buildTextForExport());
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  const emailList = () => setEmailOpen(true);

  const saveAsImage = async () => {
    if (!listRef.current) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const el = listRef.current;
      const canvas = await html2canvas(el, {
        backgroundColor: "#faf3e5",
        scale: 2,
        useCORS: true,
      });
      canvas.toBlob((blob) => {
        if (!blob) return toast.error("Image export failed");
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `shopping-list-${new Date().toISOString().slice(0, 10)}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Saved to your device");
      }, "image/png");
    } catch (e) {
      console.error(e);
      toast.error("Image export failed");
    }
  };

  const saveAsPdf = async () => {
    try {
      const [{ jsPDF }, html2canvasModule] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);
      const html2canvas = html2canvasModule.default;
      const canvas = await html2canvas(listRef.current, {
        backgroundColor: "#ffffff", scale: 2, useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 40;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 20, 20, imgWidth, imgHeight);
      pdf.save(`shopping-list-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF saved");
    } catch (e) {
      console.error(e);
      toast.error("PDF export failed");
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto py-16 text-center text-rf-ink-soft">Loading your shopping list…</div>;

  const empty = items.length === 0 && staples.length === 0;

  return (
    <div className="max-w-4xl mx-auto px-4 pt-8 pb-24">
      <AutoSeo pageKey="shopping" fallbackTitle="Shopping list" fallbackDescription="Your grocery list, aisle-grouped, remembers which recipe each item came from." />

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="overline">Recipe Filer</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-rf-ink" data-testid="shop-list-title">{listName}</h1>
          <p className="text-rf-ink-soft mt-1">
            {uncheckedCount} to buy{checkedCount > 0 ? ` · ${checkedCount} ticked` : ""}
            {staples.length > 0 ? ` · ${staples.length} in your pantry` : ""}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="shop-sharing-row">
            <ListSwitcher lists={shares} value={listOwner} onChange={switchList} onChanged={loadShares} role={role} />
            {role === "owner" ? (
              <button onClick={() => setShareOpen(true)} data-testid="shop-share-btn" className="btn-outline-ink !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5">
                <UsersThree size={16} weight="duotone" /> Share
              </button>
            ) : (
              <span className="text-xs text-rf-ink-soft inline-flex items-center gap-1" data-testid="shop-shared-badge">
                <UsersThree size={14} /> {role === "view" ? "View only" : "Shared list · changes sync live"}
                <button
                  onClick={async () => {
                    const j = shares.shared.find((x) => x.resource_id === listOwner);
                    if (!j || !window.confirm(`Leave ${j.owner_name}'s list “${j.name}”?`)) return;
                    try { await api.delete(`/shares/${j.share_id}`); toast.success("You left the shared list"); switchList(""); loadShares(); }
                    catch { toast.error("Couldn't leave"); }
                  }}
                  data-testid="shop-leave-list"
                  className="ml-2 text-red-600 font-bold hover:underline"
                >Leave</button>
              </span>
            )}
            {offline && <span className="text-xs font-bold text-amber-700" data-testid="shop-offline">Offline · showing your saved copy</span>}
            {isShared && role === "owner" && !offline && (
              <span className="text-xs text-emerald-700 font-bold inline-flex items-center gap-1.5" data-testid="shop-live-badge">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" /> Live
              </span>
            )}
          </div>
          <Link
            to="/shopping-list-help"
            data-testid="shopping-how-to-use"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-rf-orange hover:underline"
          >
            <Question size={16} weight="duotone" /> How to use the shopping list
          </Link>
        </div>

        {!empty && (
          <div className="flex flex-wrap gap-2" data-testid="shopping-export-bar">
            <button data-testid="shop-mode-open" onClick={() => setShopMode(true)} className="btn-orange !py-2 !px-4 text-sm inline-flex items-center gap-2"><ShoppingCart size={16} weight="bold" /> Shopping mode</button>
            <button data-testid="shop-copy" onClick={copyList} className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"><Copy size={16} /> Copy</button>
            <button data-testid="shop-image" onClick={saveAsImage} className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"><DownloadSimple size={16} /> Image</button>
            <button data-testid="shop-pdf" onClick={saveAsPdf} className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"><Printer size={16} /> PDF</button>
            <button data-testid="shop-email" onClick={emailList} className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"><EnvelopeSimple size={16} /> Email</button>
            {checkedCount > 0 && (
              <button data-testid="shop-clear-ticked" onClick={clearChecked} className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2 !border-red-400 !text-red-600"><Trash size={16} /> Clear ticked</button>
            )}
          </div>
        )}
      </div>

      <form onSubmit={submitQuickAdd} className="mb-4 flex gap-2" data-testid="shop-quick-add-form">
        <input
          value={quickAdd}
          onChange={(e) => setQuickAdd(e.target.value)}
          placeholder='Add anything — e.g. "2 litres of milk, bread and kitchen roll"'
          aria-label="Add items to your shopping list"
          className="input-rf flex-1"
          data-testid="shop-quick-add-input"
        />
        <button type="submit" disabled={!quickAdd.trim()} data-testid="shop-quick-add-submit" className="btn-orange !py-2 !px-4 inline-flex items-center gap-1.5 disabled:opacity-40">
          <Plus size={16} weight="bold" /> Add
        </button>
      </form>
      <p className="text-xs text-rf-ink-soft mb-6" data-testid="shop-voice-hint">
        Tip: tap the mic and say "add bread and milk to the list", "mark eggs as bought", "remove butter" or "undo".
      </p>

      {!empty && amazonEligible.length > 0 && !amazonMode && (
        <button onClick={startAmazonMode} data-testid="shop-amazon-mode" className="mb-4 btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2">
          <ShoppingCart size={16} /> Buy several on Amazon
        </button>
      )}
      {amazonMode && (
        <AmazonBulkBar
          selectedItems={amazonEligible.filter((i) => amazonSel.has(i.item_id))}
          total={amazonEligible.length}
          onSelectAll={() => setAmazonSel(new Set(amazonEligible.map((i) => i.item_id)))}
          onClear={() => setAmazonSel(new Set())}
          onCancel={() => setAmazonMode(false)}
          oneBasket={!!flags.amazon_one_basket_enabled}
        />
      )}
      <EmailShareDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        heading="Email this shopping list"
        prefillSelf
        send={(to, message) => api.post("/shopping-list/send-email", { to, message }, lq())}
      />
      {shopMode && (
        <ShoppingMode items={items} aisles={AISLES} listName={listName} live={isShared || role !== "owner"}
                      onToggle={role === "view" ? () => toast.error("This list is view only") : toggleCheck}
                      onClose={closeShopMode} />
      )}
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} resourceType="shopping_list" resourceId={listOwner || (user ? `sl_${user.user_id}` : "")} label={listName} onChanged={() => { load(); loadShares(); }} />

      {empty && (
        <div className="card-recipe text-center py-14" data-testid="shop-empty">
          <ShoppingCart size={56} weight="duotone" className="mx-auto text-rf-orange mb-4" />
          <h2 className="font-display font-black text-2xl text-rf-ink mb-2">Your shopping list is empty</h2>
          <p className="text-rf-ink-soft mb-6">Type above or tap the mic to add anything you need, or open a recipe and tap <em>Add to shopping list</em>. Items merge across recipes and remember where they came from.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/directory" className="btn-orange">Browse recipes</Link>
            <Link to="/shopping-list-help" data-testid="shop-empty-help" className="btn-outline-ink inline-flex items-center gap-2">
              <Question size={16} weight="duotone" /> How it works
            </Link>
          </div>
        </div>
      )}

      {!empty && (
        <div ref={listRef} className="card-recipe !p-6 sm:!p-8" data-testid="shopping-list-body">
          {AISLES.map(({ key, label, emoji }) => {
            const rows = grouped[key] || [];
            if (!rows.length) return null;
            return (
              <section key={key} className="mb-6 last:mb-0" data-testid={`aisle-${key}`}>
                <h2 className="font-display font-black text-xl text-rf-ink mb-3">
                  <span aria-hidden="true">{emoji}</span> {label}
                </h2>
                <ul className="space-y-1">
                  {rows.map((it) => {
                    const q = it.quantity ? `${it.quantity} ${it.unit || ""}`.trim() : "";
                    const sourceLabel = [...new Set((it.sources || [])
                      .map((s) => s.recipe_title).filter(Boolean))].join(" + ");
                    const showAmazon = it.purchase_hint !== "have" && !it.checked;
                    const isEditing = editingItemId === it.item_id;
                    return (
                      <li
                        key={it.item_id}
                        className={`group flex flex-wrap sm:flex-nowrap items-start gap-x-3 gap-y-1 py-2 border-b border-rf-ink/5 last:border-0 ${it.checked ? "opacity-50" : ""}`}
                        data-testid={`shop-item-${it.item_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!it.checked}
                          onChange={() => toggleCheck(it)}
                          aria-label={`Tick off ${it.display_name}`}
                          className="mt-1.5 w-5 h-5 accent-rf-orange shrink-0 cursor-pointer"
                          data-testid={`shop-tick-${it.item_id}`}
                        />
                        {amazonMode && showAmazon && (
                          <input
                            type="checkbox"
                            checked={amazonSel.has(it.item_id)}
                            onChange={() => toggleAmazonSel(it.item_id)}
                            aria-label={`Select ${it.display_name} to buy on Amazon`}
                            className="mt-1.5 w-5 h-5 accent-rf-ink shrink-0 cursor-pointer"
                            data-testid={`shop-amazon-select-${it.item_id}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`text-rf-ink ${it.checked ? "line-through" : ""}`}>
                            {q && <span className="font-bold">{q} </span>}
                            {it.display_name}
                          </p>
                          {it.asin && !it.checked && (
                            <p className="text-[11px] text-emerald-700 font-bold mt-0.5" data-testid={`shop-asin-${it.item_id}`}>Exact Amazon product set</p>
                          )}
                          {sourceLabel && (
                            <p className="text-xs text-rf-ink-soft mt-0.5">from {sourceLabel}</p>
                          )}

                          {isEditing && (
                            <div className="mt-2 flex flex-wrap items-center gap-2 bg-rf-amber/20 border border-rf-amber/30 rounded p-3">
                              <input
                                autoFocus
                                value={editQuery}
                                onChange={(e) => setEditQuery(e.target.value)}
                                placeholder="Search words, or paste an Amazon product link for an exact product"
                                className="input-rf !py-1.5 flex-1 min-w-[220px] text-sm"
                                data-testid="shop-edit-amazon-input"
                              />
                              <label className="text-xs text-rf-ink-soft inline-flex items-center gap-1 select-none">
                                <input type="checkbox" checked={saveAsPref} onChange={(e) => setSaveAsPref(e.target.checked)} />
                                Remember for future lists
                              </label>
                              <button
                                onClick={() => saveAmazonPref(it)}
                                data-testid="shop-edit-amazon-save"
                                className="btn-orange !py-1.5 !px-3 text-sm">Save</button>
                              <button onClick={() => setEditingItemId(null)} className="text-xs text-rf-ink-soft hover:underline">Cancel</button>
                            </div>
                          )}
                          {altsFor === it.item_id && (
                            <AlternativesPanel item={it} onSwap={swapItem} onClose={() => setAltsFor(null)} />
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1 sm:gap-2 w-full sm:w-auto pl-8 sm:pl-0 shrink-0" data-testid={`shop-actions-${it.item_id}`}>
                          {showAmazon && !isEditing && (
                            <a
                              href={it.amazon_url}
                              target="_blank"
                              rel="noopener sponsored"
                              data-testid={`shop-buy-${it.item_id}`}
                              className="text-xs font-bold px-2.5 py-1.5 rounded bg-rf-ink text-white hover:bg-black inline-flex items-center gap-1 whitespace-nowrap"
                            >
                              Buy · Amazon <CaretRight size={11} weight="bold" />
                            </a>
                          )}
                          {showAmazon && !isEditing && (
                            <button
                              onClick={() => { setEditingItemId(it.item_id); setEditQuery(it.amazon_query || it.display_name); setSaveAsPref(true); }}
                              title="Pick a specific product on Amazon"
                              data-testid={`shop-edit-${it.item_id}`}
                              className="text-rf-ink-soft hover:text-rf-orange text-xs inline-flex items-center gap-1 py-2.5 sm:py-0"
                            >
                              <Pencil size={12} /> Pick
                            </button>
                          )}
                          {!it.checked && (
                            <button
                              onClick={() => setAltsFor(altsFor === it.item_id ? null : it.item_id)}
                              title="Out of stock? Get alternative brands or items"
                              data-testid={`shop-alternatives-${it.item_id}`}
                              className="text-rf-ink-soft hover:text-rf-orange text-xs inline-flex items-center gap-1 py-2.5 sm:py-0"
                            >
                              <ArrowsLeftRight size={12} /> Swap
                            </button>
                          )}
                          <button
                            onClick={() => markStaple(it, true)}
                            title="Mark as always-have (auto-hides from future lists)"
                            data-testid={`shop-pantry-${it.item_id}`}
                            className="text-rf-ink-soft hover:text-rf-orange text-xs inline-flex items-center gap-1 py-2.5 sm:py-0"
                          >
                            <PushPin size={12} /> Pantry
                          </button>
                          <button
                            onClick={() => deleteItem(it)}
                            aria-label={`Remove ${it.display_name}`}
                            title="Remove from list"
                            data-testid={`shop-remove-${it.item_id}`}
                            className="w-9 h-9 sm:w-7 sm:h-7 inline-flex items-center justify-center rounded-full text-rf-ink-soft hover:text-white hover:bg-red-600 transition-colors"
                          ><X size={14} weight="bold" /></button>
                          {/* Layer 2 — recategorise. ⋯ opens a small aisle picker. */}
                          <div className="relative">
                            <button
                              onClick={() => setAisleMenuFor(aisleMenuFor === it.item_id ? null : it.item_id)}
                              aria-label="Move to a different aisle"
                              data-testid={`shop-menu-${it.item_id}`}
                              className="w-9 h-9 sm:w-auto sm:h-auto inline-flex items-center justify-center text-rf-ink-soft hover:text-rf-orange"
                              title="Wrong aisle? Move it"
                            >
                              <DotsThreeVertical size={18} weight="bold" />
                            </button>
                            {aisleMenuFor === it.item_id && (
                              <div
                                className="absolute right-0 top-7 z-30 w-56 bg-white rounded-xl shadow-2xl border border-rf-ink/10 py-1"
                                data-testid={`shop-aisle-menu-${it.item_id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <p className="px-3 py-2 text-[10px] uppercase tracking-widest font-black text-rf-ink-soft flex items-center gap-1.5">
                                  <ArrowsClockwise size={11} /> Move to aisle
                                </p>
                                {AISLES.map((a) => (
                                  <button
                                    key={a.key}
                                    onClick={() => moveAisle(it, a.key)}
                                    data-testid={`shop-move-${a.key}`}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-rf-cream-alt flex items-center gap-2 ${it.aisle === a.key ? "bg-rf-amber/20 font-bold text-rf-orange" : "text-rf-ink"}`}
                                  >
                                    <span className="w-5 text-center">{a.emoji}</span>
                                    <span>{a.label}</span>
                                    {it.aisle === a.key && <span className="ml-auto text-xs">current</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {/* Pantry staples strip */}
      {staples.length > 0 && (
        <div className="card-recipe mt-6 bg-rf-cream-alt/40" data-testid="shopping-staples">
          <div className="flex items-center gap-2 mb-3">
            <PushPin size={20} weight="duotone" className="text-rf-orange" />
            <h2 className="font-display font-black text-lg text-rf-ink">Your pantry — auto-hidden</h2>
          </div>
          <p className="text-sm text-rf-ink-soft mb-3">These items skip your shopping list because you always have them. Tap to remove from your pantry.</p>
          <div className="flex flex-wrap gap-2">
            {staples.map((s) => (
              <button
                key={s.item_id}
                onClick={() => markStaple(s, false)}
                data-testid={`staple-${s.item_id}`}
                className="chip !bg-white !text-rf-ink !border !border-rf-ink/15 hover:!border-rf-orange inline-flex items-center gap-1"
              ><PushPinSlash size={12} /> {s.display_name}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
